import {
  Router,
  Route,
  RootRoute,
  createBrowserHistory,
  redirect,
} from '@tanstack/react-router';
import { AppShell } from '@/components/shell/AppShell';
import { HomePage } from '@/pages/HomePage';
import { NotificationsCenter } from '@/pages/NotificationsCenter';
import { Innovations } from '@/pages/Innovations';
import { HelpPage } from '@/pages/Help';
import { Welcome as OnboardingWelcome } from '@/pages/onboarding/Welcome';
import { AuthBridge } from '@/pages/auth/Bridge';
import { SsoLanding } from '@/pages/auth/SsoLanding';
import { Login } from '@/pages/auth/Login';
import { FirstLogin } from '@/pages/auth/FirstLogin';
import { ForgotPassword } from '@/pages/auth/ForgotPassword';
import { MfaEnrolPage } from '@/pages/auth/MfaEnrol';
import { MfaChallengePage } from '@/pages/auth/MfaChallenge';

// Discovery
import { DiscoveryDashboard } from '@/pages/discovery/Dashboard';
import { DiscoveryFindings } from '@/pages/discovery/Findings';
import { DiscoveryInventory } from '@/pages/discovery/Inventory';
import { DiscoveryScans } from '@/pages/discovery/Scans';
import { DiscoveryTimeTravel } from '@/pages/discovery/TimeTravel';
import { DiscoveryLineage } from '@/pages/discovery/Lineage';
import { DiscoverySection9 } from '@/pages/discovery/Section9';
import { DiscoveryReverseDsar } from '@/pages/discovery/ReverseDsar';
import { DiscoveryHoneyPii } from '@/pages/discovery/HoneyPii';
import { DiscoveryConnectors } from '@/pages/discovery/Connectors';
import { DiscoveryConnectorBuilder } from '@/pages/discovery/ConnectorBuilder';
import { DiscoveryBootstrap } from '@/pages/discovery/Bootstrap';
import { DiscoveryClassifier } from '@/pages/discovery/Classifier';
import { DiscoveryNoDbAccess } from '@/pages/discovery/NoDbAccess';
import { DiscoveryThreatSignatures } from '@/pages/discovery/ThreatSignatures';
import { DiscoveryEdm } from '@/pages/discovery/Edm';

// Activity
import { ActivityDashboard } from '@/pages/activity/Dashboard';
import { ActivityStream } from '@/pages/activity/Stream';
import { ActivityFindings } from '@/pages/activity/Findings';
import { ActivityUeba } from '@/pages/activity/Ueba';
import { ActivitySonar } from '@/pages/activity/Sonar';
import { ActivityPwave } from '@/pages/activity/Pwave';
import { ActivityBreachReadiness } from '@/pages/activity/BreachReadiness';
import { ActivityTimeline } from '@/pages/activity/Timeline';
import { CompoundFindings as ActivityCompoundFindings } from '@/pages/activity/CompoundFindings';
import { ActivitySources } from '@/pages/activity/Sources';

// Detection
import { DetectionDashboard } from '@/pages/detection/Dashboard';
import { DetectionDecisions } from '@/pages/detection/Decisions';
import { WhyBlockedRedirect } from '@/pages/detection/WhyBlockedRedirect';
import { DetectionRules } from '@/pages/detection/Rules';
import { DetectionPlaybooks } from '@/pages/detection/Playbooks';
import { DetectionBreakGlass } from '@/pages/detection/BreakGlass';
import { DetectionSiemGallery } from '@/pages/detection/SiemGallery';

// Frontier
import { FrontierOverview } from '@/pages/frontier/Overview';
import { FrontierFederated } from '@/pages/frontier/Federated';
import { FrontierSmpc } from '@/pages/frontier/Smpc';
import { FrontierPq } from '@/pages/frontier/Pq';
import { FrontierSensors } from '@/pages/frontier/Sensors';
import { FrontierByoLlm } from '@/pages/frontier/ByoLlm';
import { FrontierChat } from '@/pages/frontier/Chat';
import { FrontierReplay } from '@/pages/frontier/Replay';

// Super Admin (platform-ops only)
import { SuperAdminDdrHealth } from '@/pages/super-admin/DdrHealth';
import { SuperAdminMasterRuleLibrary } from '@/pages/super-admin/MasterRuleLibrary';
// P8 - Phase A standalone control plane: super-admin tenants
import { Tenants as SuperAdminTenants } from '@/pages/super-admin/Tenants';
import { DeploymentPack as SuperAdminDeploymentPack } from '@/pages/super-admin/DeploymentPack';

// Admin - Integrations
import { AdminIntegrationsFocttaCore } from '@/pages/admin/IntegrationsFocttaCore';
// AdminIntegrationsIndex (legacy /admin/integrations/legacy) removed
// 2026-05-22 - MarketplacePage at /admin/integrations is the canonical
// landing surface; it already covers outbound IPs + FOCTTA Core status.
// P2 - connector marketplace
import { MarketplacePage } from '@/pages/integrations/MarketplacePage';
import { ConnectorDetailPage } from '@/pages/integrations/ConnectorDetailPage';
// P3 - auth admin (IdP / LDAP / SCIM)
import { IdpConnections } from '@/pages/admin/IdpConnections';
import { LdapSettings } from '@/pages/admin/LdapSettings';
import { ScimTokens } from '@/pages/admin/ScimTokens';
// Phase A - JWT verifiers (per-tenant registry)
import { JwtVerifiers } from '@/pages/admin/JwtVerifiers';
// P4 - notify admin (email + SMS)
import { Notify } from '@/pages/admin/Notify';
// P8 - Phase A: Discovery-owned feature-flag plane
import { FeatureFlags } from '@/pages/admin/FeatureFlags';
// Phase 5 (2026-05-22) - per-tenant DAM retention policy admin page
import { RetentionPolicy } from '@/pages/admin/RetentionPolicy';

// Admin - Foundational (BU, RBAM, etc. - Phase 5b)
import { AdminBusinessUnits } from '@/pages/admin/BusinessUnits';
import { DpoAndGrievance } from '@/pages/admin/DpoAndGrievance';
import { CompliancePostureFederation } from '@/pages/admin/CompliancePostureFederation';
import { AdminEgressLog } from '@/pages/admin/EgressLog';
import { AdminRbamMatrix } from '@/pages/admin/RbamMatrix';
import { AdminCustomRoles } from '@/pages/admin/CustomRoles';
import { AdminUsers } from '@/pages/admin/Users';
import { AdminHealth } from '@/pages/admin/Health';
import { AdminAuditSink } from '@/pages/admin/AuditSink';
import { AdminConsentSource } from '@/pages/admin/ConsentSource';
import { AdminPanVerifier } from '@/pages/admin/PanVerifier';
import { AdminQrTrustAnchor } from '@/pages/admin/QrTrustAnchor';
import { AdminBuSource } from '@/pages/admin/BuSource';
import { AdminPurposes } from '@/pages/admin/Purposes';
import { AdminAdapters } from '@/pages/admin/Adapters';
import { AdminDataSources } from '@/pages/admin/DataSources';
import { AdminModuleSetup } from '@/pages/admin/ModuleSetup';
import { AdminLicenses } from '@/pages/admin/Licenses';
import { AdminEdgeFleet } from '@/pages/admin/EdgeFleet';
import { AdminAgentEntitlement } from '@/pages/admin/AgentEntitlement';
import { AdminEdgeProvisioning } from '@/pages/admin/EdgeProvisioning';

// Compliance (Phase 8 batch A)
import { ComplianceCenter } from '@/pages/compliance/ComplianceCenter';
import { RegulatoryCoverage } from '@/pages/compliance/RegulatoryCoverage';
import { ComplianceNotices } from '@/pages/compliance/Notices';
import { ComplianceErasure } from '@/pages/compliance/Erasure';
import { ComplianceRectification } from '@/pages/compliance/Rectification';
import { ComplianceRestriction } from '@/pages/compliance/Restriction';
import { CompliancePortability } from '@/pages/compliance/Portability';
import { ComplianceObjection } from '@/pages/compliance/Objection';
import { ComplianceDpia } from '@/pages/compliance/Dpia';
import { ComplianceCcpa } from '@/pages/compliance/Ccpa';
import { ComplianceServiceProviders } from '@/pages/compliance/ServiceProviders';
import { ComplianceSdf } from '@/pages/compliance/Sdf';
import { ComplianceSensitivePi } from '@/pages/compliance/SensitivePi';
import { ComplianceBreaches } from '@/pages/compliance/Breaches';

// Admin - Industry Packs / Classifier Vocab / India Moats (Phase 6)
import { AdminIndustryPacks } from '@/pages/admin/IndustryPacks';
import { IndustryOverviewPage } from '@/pages/admin/IndustryOverview';
import { EffectiveConfigPage } from '@/pages/admin/EffectiveConfig';
import { NotificationPreferencesPage } from '@/pages/admin/NotificationPreferences';
import { AuditLogsPage } from '@/pages/admin/AuditLogs';
import { AdminClassifierVocab } from '@/pages/admin/ClassifierVocab';
import { AdminIndiaMoats } from '@/pages/admin/IndiaMoats';

const rootRoute = new RootRoute({ component: AppShell });

const homeRoute = new Route({ getParentRoute: () => rootRoute, path: '/', component: HomePage });
const notificationsRoute = new Route({ getParentRoute: () => rootRoute, path: '/notifications', component: NotificationsCenter });
const innovationsRoute = new Route({ getParentRoute: () => rootRoute, path: '/innovations', component: Innovations });
const helpRoute = new Route({ getParentRoute: () => rootRoute, path: '/help', component: HelpPage });
// Slice 2.2 - Welcome / onboarding journey for first-time IT Admins.
const onboardingWelcomeRoute = new Route({ getParentRoute: () => rootRoute, path: '/welcome', component: OnboardingWelcome });
// Phase B compatibility alias (2026-05-21) - Core's /v1/discovery/handoff
// sends `next=/onboarding/welcome` in the bridge fragment per its
// own SPA convention. Discovery's canonical route is `/welcome` but we
// mount the same component at `/onboarding/welcome` so cross-app
// callers landing here don't 404. Both paths render identically.
const onboardingWelcomeAliasRoute = new Route({ getParentRoute: () => rootRoute, path: '/onboarding/welcome', component: OnboardingWelcome });
// Phase A (doc 21 §3.2) - Cross-app SSO bridge landing page. Reads a
// short-lived bridge JWT from URL fragment (#t=...), POSTs to
// /v1/auth/exchange, persists Discovery session, redirects to /onboarding.
// AppShell bypasses its auth-gate + chrome for both bridge paths.
// Two routes, one component: Core emits the bare-root form today and will emit
// the /discovery-prefixed form after the Pattern A cutover. Accepting both
// removes the window where a switched sender meets an un-widened receiver -
// see lib/bridge-paths.ts for why the receiver goes first.
//
// The prefixed route matters ONLY while this bundle is mounted at the ROOT.
// Once VITE_ROUTER_BASEPATH=/discovery is set, TanStack already resolves the
// bare '/auth/bridge' route at '/discovery/auth/bridge', and this second route
// would resolve at '/discovery/discovery/auth/bridge' - unreachable, harmless,
// and exactly the doubling the July DSPM rename existed to avoid. It stays
// because the migration window needs it; remove it only once Core has switched
// AND this bundle is built with the basepath.
const authBridgeRoute = new Route({ getParentRoute: () => rootRoute, path: '/auth/bridge', component: AuthBridge });
const authBridgePrefixedRoute = new Route({ getParentRoute: () => rootRoute, path: '/discovery/auth/bridge', component: AuthBridge });

// Workstream B (2026-07-12) - SSO landing page. The SAML ACS / OIDC callback
// redirects here with a full Discovery session JWT in the URL fragment
// (#access_token=...); the page establishes the session and redirects in.
// AppShell bypasses its auth-gate + chrome for /sso/landing.
const ssoLandingRoute = new Route({ getParentRoute: () => rootRoute, path: '/sso/landing', component: SsoLanding });

// Phase 4 (2026-05-22) - Discovery customer auth routes. Independent
// of Core's bridge SSO (which still works for one-click "Open Discovery"
// from Core) - these support direct sign-in for users who bookmark
// <slug>.discovery.foctta.com or land via the activation email link.
const loginRoute = new Route({ getParentRoute: () => rootRoute, path: '/login', component: Login });
const firstLoginRoute = new Route({ getParentRoute: () => rootRoute, path: '/first-login', component: FirstLogin });
const forgotPasswordRoute = new Route({ getParentRoute: () => rootRoute, path: '/forgot-password', component: ForgotPassword });
const mfaEnrolRoute = new Route({ getParentRoute: () => rootRoute, path: '/auth/mfa-enroll', component: MfaEnrolPage });
const mfaChallengeRoute = new Route({ getParentRoute: () => rootRoute, path: '/auth/mfa-challenge', component: MfaChallengePage });

// /signup was removed 2026-05-22 - Core is the canonical signup.

// Discovery
const discoveryRoot = new Route({ getParentRoute: () => rootRoute, path: '/dspm', component: DiscoveryDashboard });
const discoveryFindings = new Route({ getParentRoute: () => rootRoute, path: '/dspm/findings', component: DiscoveryFindings });
const discoveryInventory = new Route({ getParentRoute: () => rootRoute, path: '/dspm/inventory', component: DiscoveryInventory });
// Alias (doc 33): /discovery/assets is an intuitive URL a CISO or an old
// bookmark / Core deep-link may hit. Point it at the same Asset Inventory
// rather than 404-ing.
const discoveryAssetsAlias = new Route({ getParentRoute: () => rootRoute, path: '/dspm/assets', component: DiscoveryInventory });
const discoveryScans = new Route({ getParentRoute: () => rootRoute, path: '/dspm/scans', component: DiscoveryScans });
const discoveryTimeTravel = new Route({ getParentRoute: () => rootRoute, path: '/dspm/time-travel', component: DiscoveryTimeTravel });
const discoveryLineage = new Route({ getParentRoute: () => rootRoute, path: '/dspm/lineage', component: DiscoveryLineage });
const discoverySection9 = new Route({ getParentRoute: () => rootRoute, path: '/dspm/section-9', component: DiscoverySection9 });
const discoveryReverseDsar = new Route({ getParentRoute: () => rootRoute, path: '/dspm/reverse-dsar', component: DiscoveryReverseDsar });
const discoveryHoneyPii = new Route({ getParentRoute: () => rootRoute, path: '/dspm/honey-pii', component: DiscoveryHoneyPii });
const discoveryConnectors = new Route({ getParentRoute: () => rootRoute, path: '/dspm/connectors', component: DiscoveryConnectors });
const discoveryConnectorBuilder = new Route({ getParentRoute: () => rootRoute, path: '/dspm/target-builder', component: DiscoveryConnectorBuilder });
const discoveryBootstrap = new Route({ getParentRoute: () => rootRoute, path: '/dspm/bootstrap', component: DiscoveryBootstrap });
const discoveryClassifier = new Route({ getParentRoute: () => rootRoute, path: '/dspm/classifier', component: DiscoveryClassifier });
const discoveryNoDbAccess = new Route({ getParentRoute: () => rootRoute, path: '/dspm/no-db-access', component: DiscoveryNoDbAccess });
const discoveryThreatSignatures = new Route({ getParentRoute: () => rootRoute, path: '/dspm/threat-signatures', component: DiscoveryThreatSignatures });
const discoveryEdm = new Route({ getParentRoute: () => rootRoute, path: '/dspm/edm', component: DiscoveryEdm });

// Legacy /discovery/* -> /dspm/* redirects (Option B rename, C-spike 2026-07-12).
// The DSPM module's internal prefix moved off `/discovery` so it doesn't
// double to `/discovery/discovery/*` when mounted under Core at basepath
// `/discovery`. These keep old console bookmarks + deep links (and any old
// doubled `/discovery/discovery/*` under the mount) working - a client-side
// 301. beforeLoad throws before the page renders, so there's no flash.
const DSPM_LEGACY_REDIRECTS: ReadonlyArray<readonly [string, string]> = [
  ['/discovery', '/dspm'],
  ['/discovery/findings', '/dspm/findings'],
  ['/discovery/inventory', '/dspm/inventory'],
  ['/discovery/assets', '/dspm/assets'],
  ['/discovery/scans', '/dspm/scans'],
  ['/discovery/time-travel', '/dspm/time-travel'],
  ['/discovery/lineage', '/dspm/lineage'],
  ['/discovery/section-9', '/dspm/section-9'],
  ['/discovery/reverse-dsar', '/dspm/reverse-dsar'],
  ['/discovery/honey-pii', '/dspm/honey-pii'],
  ['/discovery/connectors', '/dspm/connectors'],
  ['/discovery/target-builder', '/dspm/target-builder'],
  ['/discovery/bootstrap', '/dspm/bootstrap'],
  ['/discovery/classifier', '/dspm/classifier'],
  ['/discovery/no-db-access', '/dspm/no-db-access'],
  ['/discovery/threat-signatures', '/dspm/threat-signatures'],
  ['/discovery/edm', '/dspm/edm'],
];
const dspmLegacyRedirectRoutes = DSPM_LEGACY_REDIRECTS.map(
  ([from, to]) =>
    new Route({
      getParentRoute: () => rootRoute,
      path: from,
      // Preserve any query params on the old URL through the redirect.
      beforeLoad: ({ search }) => {
        throw redirect({ to: to as never, search: search as never, replace: true });
      },
    }),
);

// Activity
const activityRoot = new Route({ getParentRoute: () => rootRoute, path: '/activity', component: ActivityDashboard });
const activityStream = new Route({ getParentRoute: () => rootRoute, path: '/activity/stream', component: ActivityStream });
const activityFindings = new Route({ getParentRoute: () => rootRoute, path: '/activity/findings', component: ActivityFindings });
const activityUeba = new Route({ getParentRoute: () => rootRoute, path: '/activity/ueba', component: ActivityUeba });
const activitySonar = new Route({ getParentRoute: () => rootRoute, path: '/activity/sonar', component: ActivitySonar });
const activityPwave = new Route({ getParentRoute: () => rootRoute, path: '/activity/p-wave', component: ActivityPwave });
const activityBreach = new Route({ getParentRoute: () => rootRoute, path: '/activity/breach-readiness', component: ActivityBreachReadiness });
const activityTimeline = new Route({ getParentRoute: () => rootRoute, path: '/activity/timeline', component: ActivityTimeline });
const activitySources = new Route({ getParentRoute: () => rootRoute, path: '/activity/sources', component: ActivitySources });
// 2026-05-29 - DAM kill-chain UI for the compound-severity correlator (backend 8a3ad1f).
const activityCompound = new Route({ getParentRoute: () => rootRoute, path: '/activity/kill-chain', component: ActivityCompoundFindings });

// Detection
const detectionRoot = new Route({ getParentRoute: () => rootRoute, path: '/detection', component: DetectionDashboard });
const detectionDecisions = new Route({ getParentRoute: () => rootRoute, path: '/detection/decisions', component: DetectionDecisions });
const detectionRules = new Route({ getParentRoute: () => rootRoute, path: '/detection/rules', component: DetectionRules });
const detectionPlaybooks = new Route({ getParentRoute: () => rootRoute, path: '/detection/playbooks', component: DetectionPlaybooks });
const detectionBreakGlass = new Route({ getParentRoute: () => rootRoute, path: '/detection/break-glass', component: DetectionBreakGlass });
const detectionSiem = new Route({ getParentRoute: () => rootRoute, path: '/detection/siem-gallery', component: DetectionSiemGallery });
// The URL the DDR gateway puts in a blocked query's error hint. It served
// nothing until 8 Sep 2026, so the one link a blocked DBA is given opened a
// dead page. Forwards to the decision record for that request id.
const whyBlocked = new Route({ getParentRoute: () => rootRoute, path: '/operator/why-blocked/why/$reqId', component: WhyBlockedRedirect });

// Frontier
const frontierRoot = new Route({ getParentRoute: () => rootRoute, path: '/frontier', component: FrontierOverview });
const frontierFederated = new Route({ getParentRoute: () => rootRoute, path: '/frontier/federated', component: FrontierFederated });
const frontierSmpc = new Route({ getParentRoute: () => rootRoute, path: '/frontier/smpc', component: FrontierSmpc });
const frontierPq = new Route({ getParentRoute: () => rootRoute, path: '/frontier/pq', component: FrontierPq });
const frontierSensors = new Route({ getParentRoute: () => rootRoute, path: '/frontier/sensors', component: FrontierSensors });
const frontierByoLlm = new Route({ getParentRoute: () => rootRoute, path: '/frontier/byo-llm', component: FrontierByoLlm });
const frontierChat = new Route({ getParentRoute: () => rootRoute, path: '/frontier/chat', component: FrontierChat });
const frontierReplay = new Route({ getParentRoute: () => rootRoute, path: '/frontier/replay', component: FrontierReplay });

// Super Admin (platform-ops only)
const superAdminHealth = new Route({ getParentRoute: () => rootRoute, path: '/super-admin/ddr-health', component: SuperAdminDdrHealth });
const superAdminLibrary = new Route({ getParentRoute: () => rootRoute, path: '/super-admin/master-rule-library', component: SuperAdminMasterRuleLibrary });
// P8 - Phase A standalone tenant control plane
const superAdminTenants = new Route({ getParentRoute: () => rootRoute, path: '/super-admin/tenants', component: SuperAdminTenants });
const superAdminDeploymentPack = new Route({ getParentRoute: () => rootRoute, path: '/super-admin/deployment-pack', component: SuperAdminDeploymentPack });

// Admin → Integrations → FOCTTA core (browser-driven Connect wizard).
// The legacy /admin/integrations/legacy index was removed in Phase 0
// (2026-05-22) - the marketplace at /admin/integrations subsumes it.
const adminIntegrationsFocttaCore = new Route({ getParentRoute: () => rootRoute, path: '/admin/integrations/foctta-core', component: AdminIntegrationsFocttaCore });

// P2 - connector marketplace (new landing for /admin/integrations + per-connector detail)
const adminMarketplace = new Route({ getParentRoute: () => rootRoute, path: '/admin/integrations', component: MarketplacePage });
const adminConnectorDetail = new Route({ getParentRoute: () => rootRoute, path: '/admin/integrations/connector/$connectorId', component: ConnectorDetailPage });

// P3 - Admin → Auth (IdP / LDAP / SCIM)
const adminIdpConnections = new Route({ getParentRoute: () => rootRoute, path: '/admin/auth/idp', component: IdpConnections });
const adminLdapSettings = new Route({ getParentRoute: () => rootRoute, path: '/admin/auth/ldap', component: LdapSettings });
const adminScimTokens = new Route({ getParentRoute: () => rootRoute, path: '/admin/auth/scim', component: ScimTokens });
// Phase A - Admin → Auth → JWT Verifiers (per-tenant verifier registry).
const adminJwtVerifiers = new Route({ getParentRoute: () => rootRoute, path: '/admin/auth/jwt-verifiers', component: JwtVerifiers });

// P4 - Admin → Notify (Email + SMS)
const adminNotify = new Route({ getParentRoute: () => rootRoute, path: '/admin/notify', component: Notify });

// P8 - Admin → Feature Flags (Discovery-owned plane)
const adminFeatureFlags = new Route({ getParentRoute: () => rootRoute, path: '/admin/feature-flags', component: FeatureFlags });

// Phase 5 (2026-05-22) - Admin → DAM Retention Policy
const adminRetentionPolicy = new Route({ getParentRoute: () => rootRoute, path: '/admin/retention-policy', component: RetentionPolicy });

// Admin → Foundational → Business Units (Phase 5b item 3)
const adminBusinessUnits = new Route({ getParentRoute: () => rootRoute, path: '/admin/business-units', component: AdminBusinessUnits });

// Admin → Compliance posture (T1.5.B - 2026-05-24) - DPDPA §8 DPO + §13 grievance officer
const adminDpoGrievance = new Route({ getParentRoute: () => rootRoute, path: '/admin/dpo-grievance', component: DpoAndGrievance });

// Admin → Compliance Posture Federation (F - 2026-05-25) - unified
// dashboard showing all 7 federation entity counts.
const adminCompliancePostureFederation = new Route({
  getParentRoute: () => rootRoute,
  path: '/admin/compliance-posture',
  component: CompliancePostureFederation,
});

// Admin → Foundational → Egress log (Phase 5b item 8)
const adminEgressLog = new Route({ getParentRoute: () => rootRoute, path: '/admin/egress-log', component: AdminEgressLog });

// Admin → Foundational - RBAM matrix / Purposes / Adapters / Licenses (Phase 5b items 4, 6, 9, 7)
const adminRbamMatrix = new Route({ getParentRoute: () => rootRoute, path: '/admin/rbam', component: AdminRbamMatrix });
const adminCustomRoles = new Route({ getParentRoute: () => rootRoute, path: '/admin/custom-roles', component: AdminCustomRoles });
// T1.1 (2026-05-28) - Discovery-native /admin/users surface. Backend
// shipped in c409666; this finishes the Independence-Principle gap
// so standalone tenants don't depend on Core to manage users.
const adminUsers = new Route({ getParentRoute: () => rootRoute, path: '/admin/users', component: AdminUsers });
// T1.2 (2026-05-28) - Tenant-facing health snapshot (audit chain +
// scanners + edge fleet + audit-vault + recent errors + compound).
const adminHealth = new Route({ getParentRoute: () => rootRoute, path: '/admin/health', component: AdminHealth });
// T1.4 (2026-05-28) - AuditSink admin (tenant SIEM webhook config).
const adminAuditSink = new Route({ getParentRoute: () => rootRoute, path: '/admin/audit-sink', component: AdminAuditSink });
// Alt-CMP consent-source admin: lets a standalone tenant point the consent
// firewall at their own CMP instead of needing a seeded DB row.
const adminConsentSource = new Route({ getParentRoute: () => rootRoute, path: '/admin/consent-source', component: AdminConsentSource });
const adminPanVerifier = new Route({ getParentRoute: () => rootRoute, path: '/admin/pan-verifier', component: AdminPanVerifier });
const adminQrTrustAnchor = new Route({ getParentRoute: () => rootRoute, path: '/admin/qr-verification', component: AdminQrTrustAnchor });
// Bulk, non-core business-unit loading for standalone tenants.
const adminBuSource = new Route({ getParentRoute: () => rootRoute, path: '/admin/bu-source', component: AdminBuSource });
const adminPurposes = new Route({ getParentRoute: () => rootRoute, path: '/admin/purposes', component: AdminPurposes });
const adminAdapters = new Route({ getParentRoute: () => rootRoute, path: '/admin/adapters', component: AdminAdapters });
const adminDataSources = new Route({ getParentRoute: () => rootRoute, path: '/admin/data-sources', component: AdminDataSources });
const adminModuleSetup = new Route({ getParentRoute: () => rootRoute, path: '/admin/module-setup', component: AdminModuleSetup });
const adminLicenses = new Route({ getParentRoute: () => rootRoute, path: '/admin/licenses', component: AdminLicenses });

// Admin → Edge Ops (Phase 7 batch A)
const adminEdgeFleet = new Route({ getParentRoute: () => rootRoute, path: '/admin/edge-fleet', component: AdminEdgeFleet });
const adminAgentEntitlement = new Route({ getParentRoute: () => rootRoute, path: '/admin/agent-entitlement', component: AdminAgentEntitlement });
const adminEdgeProvisioning = new Route({ getParentRoute: () => rootRoute, path: '/admin/edge-provisioning', component: AdminEdgeProvisioning });

// Compliance - Phase 8 batch A
const complianceRoute = new Route({ getParentRoute: () => rootRoute, path: '/compliance', component: ComplianceCenter });
const complianceRegulatoryCoverageRoute = new Route({ getParentRoute: () => rootRoute, path: '/compliance/regulatory-coverage', component: RegulatoryCoverage });
const complianceNoticesRoute = new Route({ getParentRoute: () => rootRoute, path: '/compliance/notices', component: ComplianceNotices });
const complianceErasureRoute = new Route({ getParentRoute: () => rootRoute, path: '/compliance/erasure', component: ComplianceErasure });
const complianceRectificationRoute = new Route({ getParentRoute: () => rootRoute, path: '/compliance/rectification', component: ComplianceRectification });
const complianceRestrictionRoute = new Route({ getParentRoute: () => rootRoute, path: '/compliance/restriction', component: ComplianceRestriction });
const compliancePortabilityRoute = new Route({ getParentRoute: () => rootRoute, path: '/compliance/portability', component: CompliancePortability });
const complianceObjectionRoute = new Route({ getParentRoute: () => rootRoute, path: '/compliance/objection', component: ComplianceObjection });
const complianceDpiaRoute = new Route({ getParentRoute: () => rootRoute, path: '/compliance/dpia', component: ComplianceDpia });
const complianceCcpaRoute = new Route({ getParentRoute: () => rootRoute, path: '/compliance/ccpa', component: ComplianceCcpa });
const complianceProvidersRoute = new Route({ getParentRoute: () => rootRoute, path: '/compliance/service-providers', component: ComplianceServiceProviders });
const complianceSdfRoute = new Route({ getParentRoute: () => rootRoute, path: '/compliance/sdf', component: ComplianceSdf });
const complianceSensitivePiRoute = new Route({ getParentRoute: () => rootRoute, path: '/compliance/sensitive-pi', component: ComplianceSensitivePi });
const complianceBreachesRoute = new Route({ getParentRoute: () => rootRoute, path: '/compliance/breaches', component: ComplianceBreaches });

// Admin → Industry Packs / Classifier Vocab / India Moats (Phase 6)
const adminIndustryPacks = new Route({ getParentRoute: () => rootRoute, path: '/admin/industry-packs', component: AdminIndustryPacks });
const adminIndustryOverview = new Route({ getParentRoute: () => rootRoute, path: '/admin/industry-overview', component: IndustryOverviewPage });
const adminEffectiveConfig = new Route({ getParentRoute: () => rootRoute, path: '/admin/effective-config', component: EffectiveConfigPage });
const adminNotificationPrefs = new Route({ getParentRoute: () => rootRoute, path: '/admin/notification-preferences', component: NotificationPreferencesPage });
const adminAuditLogs = new Route({ getParentRoute: () => rootRoute, path: '/admin/audit-logs', component: AuditLogsPage });
const adminClassifierVocab = new Route({ getParentRoute: () => rootRoute, path: '/admin/classifier-vocab', component: AdminClassifierVocab });
const adminIndiaMoats = new Route({ getParentRoute: () => rootRoute, path: '/admin/india-moats', component: AdminIndiaMoats });

const routeTree = rootRoute.addChildren([
  homeRoute, notificationsRoute, innovationsRoute, helpRoute, onboardingWelcomeRoute, onboardingWelcomeAliasRoute, authBridgeRoute, authBridgePrefixedRoute,
  ssoLandingRoute,
  loginRoute, firstLoginRoute, forgotPasswordRoute, mfaEnrolRoute, mfaChallengeRoute,
  discoveryRoot, discoveryFindings, discoveryInventory, discoveryAssetsAlias, discoveryScans, discoveryTimeTravel, discoveryLineage,
  discoverySection9, discoveryReverseDsar, discoveryHoneyPii, discoveryConnectors, discoveryConnectorBuilder,
  discoveryBootstrap, discoveryClassifier, discoveryNoDbAccess, discoveryThreatSignatures, discoveryEdm,
  ...dspmLegacyRedirectRoutes,
  activityRoot, activityStream, activityFindings, activityUeba, activitySonar, activityPwave, activityBreach, activityTimeline, activitySources, activityCompound,
  detectionRoot, detectionDecisions, detectionRules, detectionPlaybooks, detectionBreakGlass, detectionSiem,
  whyBlocked,
  frontierRoot, frontierFederated, frontierSmpc, frontierPq, frontierSensors, frontierByoLlm, frontierChat, frontierReplay,
  superAdminHealth, superAdminLibrary, superAdminTenants, superAdminDeploymentPack,
  adminMarketplace,
  adminConnectorDetail,
  adminIntegrationsFocttaCore,
  adminIdpConnections,
  adminLdapSettings,
  adminScimTokens,
  adminJwtVerifiers,
  adminNotify,
  adminFeatureFlags,
  adminRetentionPolicy,
  adminBusinessUnits,
  adminDpoGrievance,
  adminCompliancePostureFederation,
  adminEgressLog,
  adminRbamMatrix,
  adminCustomRoles,
  adminUsers,
  adminHealth,
  adminAuditSink,
  adminConsentSource,
  adminPanVerifier,
  adminQrTrustAnchor,
  adminBuSource,
  adminPurposes,
  adminAdapters,
  adminDataSources,
  adminModuleSetup,
  adminLicenses,
  adminEdgeFleet,
  adminAgentEntitlement,
  adminEdgeProvisioning,
  complianceRoute,
  complianceRegulatoryCoverageRoute,
  complianceNoticesRoute,
  complianceErasureRoute,
  complianceRectificationRoute,
  complianceRestrictionRoute,
  compliancePortabilityRoute,
  complianceObjectionRoute,
  complianceDpiaRoute,
  complianceCcpaRoute,
  complianceProvidersRoute,
  complianceSdfRoute,
  complianceSensitivePiRoute,
  complianceBreachesRoute,
  adminIndustryPacks, adminIndustryOverview, adminEffectiveConfig, adminNotificationPrefs, adminAuditLogs,
  adminClassifierVocab,
  adminIndiaMoats,
]);

/**
 * Workstream C (2026-07-12) - router factory so the SAME route tree can run
 * (a) standalone at the domain root (basepath undefined - today's
 * discovery.foctta.com console and the shell-app demo host), or
 * (b) mounted INSIDE Core's app at a path prefix (basepath '/discovery' -
 * the Core-as-host inline mount; Core's splat route renders the remote and
 * TanStack prefixes every resolved href/match with the basepath).
 * The C-spike (Core-led) passes `basepath` via DiscoveryRemoteAppProps in
 * routes.federated.tsx; standalone keeps the module singleton below.
 */
export function createDiscoveryRouter(basepath?: string) {
  return new Router({
    routeTree,
    history: createBrowserHistory(),
    defaultPreload: 'intent',
    ...(basepath ? { basepath } : {}),
  });
}

/**
 * Build-time mount prefix, injected by vite.config.ts (`__ROUTER_BASEPATH__`).
 *
 * Empty means the domain root, which is what `discovery.foctta.com` and a
 * customer's own VPS serve - so the default behaviour is unchanged and this is
 * additive. Setting `VITE_ROUTER_BASEPATH=/discovery` at build time produces the
 * Pattern A bundle, where every route resolves under that prefix:
 * `<slug>.foctta.com/discovery/dspm/findings`.
 *
 * This is why the July rename moved DSPM's internal prefix from `/discovery/*`
 * to `/dspm/*` - under this basepath the old paths would have doubled into
 * `/discovery/discovery/findings`. The legacy redirects above keep old
 * bookmarks working either way.
 *
 * Guarded with `typeof` because the constant is absent under plain `tsc`, in
 * vitest, and in any consumer that imports this module without Vite's define
 * step - an unguarded reference throws ReferenceError at import time and takes
 * the whole app down rather than falling back to root.
 */
declare const __ROUTER_BASEPATH__: string;
export const ROUTER_BASEPATH: string =
  typeof __ROUTER_BASEPATH__ !== 'undefined' && __ROUTER_BASEPATH__ ? __ROUTER_BASEPATH__ : '';

export const router = createDiscoveryRouter(ROUTER_BASEPATH || undefined);

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
